import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

function themeColors() {
  const dark = document.documentElement.classList.contains('dark');
  return {
    dark,
    text:       dark ? '#9ca3af' : '#6b7280',
    grid:       dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    line:       dark ? '#a78bfa' : '#7c3aed',
    fill:       dark ? 'rgba(167,139,250,0.10)' : 'rgba(124,58,237,0.08)',
    pointNorm:  dark ? '#60a5fa' : '#2563eb',
    pointLaag:  dark ? '#f87171' : '#dc2626',
    pointHoog:  dark ? '#f87171' : '#dc2626',
    tooltipBg:  dark ? '#1f2937' : '#111827',
    refBand:    dark ? 'rgba(52,211,153,0.10)' : 'rgba(16,185,129,0.08)',
  };
}

function baseComponent(render) {
  return {
    _chart:    null,
    _observer: null,

    init() {
      this.$nextTick(() => this.render());
      this._observer = new MutationObserver(() => this.render());
      this._observer.observe(document.documentElement, {
        attributes:     true,
        attributeFilter: ['class'],
      });
    },

    destroy() {
      this._observer?.disconnect();
      this._chart?.destroy();
    },

    render,
  };
}

function datumKort(iso) {
  const dt = new Date(String(iso).replace(' ', 'T'));
  return Number.isNaN(dt.getTime())
    ? String(iso)
    : dt.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Lijndiagram voor één bloedtestmarker over de tijd.
 *
 * cfg: {
 *   punten:  [{ datum, waarde, vlag, min_ref, max_ref }]  — chronologisch
 *   naam:    string  — naam van de marker
 *   eenheid: string  — bv. 'g/dL'
 *   labels:  { ref_band }
 * }
 *
 * Toont een referentieband (min/max) als achtergrond wanneer tenminste
 * één punt een ref_min of ref_max heeft. Punten worden ingekleurd op vlag
 * (rood = laag/hoog, blauw = normaal).
 */
export function bloedtestTrendChart(cfg) {
  return baseComponent(function () {
    this._chart?.destroy();
    this._chart = null;

    const canvas = this.$refs.chart;
    if (!canvas || !cfg.punten || cfg.punten.length < 2) return;

    const t = themeColors();

    // Referentiegrenzen: eerste punt dat een ref heeft wint.
    let minRef = null;
    let maxRef = null;
    for (const p of cfg.punten) {
      if (p.min_ref != null && minRef == null) minRef = parseFloat(p.min_ref);
      if (p.max_ref != null && maxRef == null) maxRef = parseFloat(p.max_ref);
      if (minRef != null && maxRef != null) break;
    }

    const puntKleur = (vlag) => {
      if (vlag === 'laag' || vlag === 'hoog') return t.pointLaag;
      return t.pointNorm;
    };

    // Referentieband-plugin (vertic zone)
    const refBandPlugin = {
      id: 'refBand',
      beforeDatasetsDraw(chart) {
        if (minRef == null && maxRef == null) return;
        const { ctx, chartArea, scales } = chart;
        if (!chartArea || !scales.y) return;

        const yMin = minRef != null ? scales.y.getPixelForValue(minRef) : chartArea.bottom;
        const yMax = maxRef != null ? scales.y.getPixelForValue(maxRef) : chartArea.top;

        ctx.save();
        ctx.fillStyle = t.refBand;
        ctx.fillRect(
          chartArea.left,
          Math.min(yMin, yMax),
          chartArea.right - chartArea.left,
          Math.abs(yMax - yMin),
        );
        ctx.restore();
      },
    };

    this._chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: cfg.punten.map((p) => p.datum),
        datasets: [
          {
            label: cfg.naam,
            data:  cfg.punten.map((p) => parseFloat(p.waarde)),
            borderColor: t.line,
            backgroundColor: t.fill,
            pointBackgroundColor: cfg.punten.map((p) => puntKleur(p.vlag)),
            pointBorderColor:    cfg.punten.map((p) => puntKleur(p.vlag)),
            pointRadius: 4,
            pointHoverRadius: 6,
            borderWidth: 2,
            fill: true,
            tension: 0.25,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: t.tooltipBg,
            padding: 10,
            cornerRadius: 6,
            callbacks: {
              title: (items) => datumKort(items[0].label),
              label: (ctx) => {
                const p     = cfg.punten[ctx.dataIndex];
                const vlagTxt = p.vlag !== 'normaal' ? ` — ${p.vlag.toUpperCase()}` : '';
                return `${ctx.parsed.y} ${cfg.eenheid}${vlagTxt}`;
              },
              afterLabel: (ctx) => {
                const p = cfg.punten[ctx.dataIndex];
                if (p.min_ref != null || p.max_ref != null) {
                  const min = p.min_ref != null ? p.min_ref : '?';
                  const max = p.max_ref != null ? p.max_ref : '?';
                  return `${cfg.labels.ref_band}: ${min}–${max} ${cfg.eenheid}`;
                }
                return undefined;
              },
            },
          },
        },
        scales: {
          x: {
            ticks: {
              color: t.text,
              maxTicksLimit: 8,
              callback: function (val) {
                return datumKort(this.getLabelForValue(val));
              },
            },
            grid: { display: false },
          },
          y: {
            title: { display: true, text: cfg.eenheid, color: t.text },
            ticks: { color: t.text },
            grid:  { color: t.grid },
          },
        },
      },
      plugins: [refBandPlugin],
    });
  });
}
